import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import PageHeader from './ui/PageHeader';
import {
  Plug, Shield, Users, Briefcase, RefreshCw, CheckCircle, XCircle,
  AlertTriangle, Clock, DollarSign, FileText, Building, Link2,
  Unlink, Settings, ChevronRight, Plus, ExternalLink, Calendar,
  ClipboardList, TrendingUp, AlertCircle, Zap, Database
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status colors
const STATUS_COLORS = {
  connected: 'bg-green-100 text-green-800 border-green-200',
  disconnected: 'bg-gray-100 text-gray-800 border-gray-200',
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  error: 'bg-red-100 text-red-800 border-red-200'
};

const IntegrationsHub = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [providers, setProviders] = useState({ insurance: [], hr: [], project_management: [] });
  const [integrations, setIntegrations] = useState({ insurance: [], hr: [], project_management: [] });
  const [insuranceDashboard, setInsuranceDashboard] = useState(null);
  const [hrDashboard, setHrDashboard] = useState(null);
  const [pmDashboard, setPmDashboard] = useState(null);
  
  // Connection modal state
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectingType, setConnectingType] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');

  // Scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [providersRes, integrationsRes, insuranceRes, hrRes, pmRes] = await Promise.all([
        axios.get(`${API}/integrations/providers`).catch(() => ({ data: {} })),
        axios.get(`${API}/integrations/list`).catch(() => ({ data: { integrations: {} } })),
        axios.get(`${API}/integrations/insurance/dashboard`).catch(() => ({ data: null })),
        axios.get(`${API}/integrations/hr/dashboard`).catch(() => ({ data: null })),
        axios.get(`${API}/integrations/pm/dashboard`).catch(() => ({ data: null }))
      ]);
      
      setProviders(providersRes.data || { insurance: [], hr: [], project_management: [] });
      setIntegrations(integrationsRes.data?.integrations || { insurance: [], hr: [], project_management: [] });
      setInsuranceDashboard(insuranceRes.data);
      setHrDashboard(hrRes.data);
      setPmDashboard(pmRes.data);
    } catch (error) {
      console.error('Error loading integration data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openConnectModal = (type) => {
    setConnectingType(type);
    setSelectedProvider('');
    setApiKey('');
    setApiSecret('');
    setShowConnectModal(true);
  };

  const connectIntegration = async () => {
    if (!selectedProvider) {
      toast({ title: "Error", description: "Please select a provider", variant: "destructive" });
      return;
    }

    try {
      await axios.post(`${API}/integrations/connect`, null, {
        params: {
          company_id: 'demo',
          integration_type: connectingType,
          provider: selectedProvider,
          api_key: apiKey || null,
          api_secret: apiSecret || null
        }
      });
      
      toast({ title: "Connected!", description: `Successfully connected to ${selectedProvider}` });
      setShowConnectModal(false);
      loadData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to connect integration", variant: "destructive" });
    }
  };

  const disconnectIntegration = async (integrationId) => {
    try {
      await axios.post(`${API}/integrations/disconnect/${integrationId}`);
      toast({ title: "Disconnected", description: "Integration disconnected successfully" });
      loadData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to disconnect", variant: "destructive" });
    }
  };

  const syncIntegration = async (integrationId) => {
    try {
      await axios.post(`${API}/integrations/sync/${integrationId}`);
      toast({ title: "Sync Started", description: "Data sync initiated" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to sync", variant: "destructive" });
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Universal Page Header */}
      <PageHeader
        title="Integrations Hub"
        subtitle="Connect Insurance, HR, and Project Management systems"
        icon={Plug}
        variant="purple"
        onRefresh={loadData}
        loading={loading}
      />

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-xs">Insurance</p>
                <p className="text-2xl font-bold">{integrations.insurance?.length || 0}</p>
              </div>
              <Shield className="w-8 h-8 text-blue-200" />
            </div>
            <p className="text-xs text-blue-100 mt-1">Connected providers</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-xs">HR Systems</p>
                <p className="text-2xl font-bold">{integrations.hr?.length || 0}</p>
              </div>
              <Users className="w-8 h-8 text-green-200" />
            </div>
            <p className="text-xs text-green-100 mt-1">Employee sync enabled</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-xs">Project Mgmt</p>
                <p className="text-2xl font-bold">{integrations.project_management?.length || 0}</p>
              </div>
              <Briefcase className="w-8 h-8 text-purple-200" />
            </div>
            <p className="text-xs text-purple-100 mt-1">Task tracking active</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-xs">Data Synced</p>
                <p className="text-2xl font-bold">Real-time</p>
              </div>
              <Zap className="w-8 h-8 text-orange-200" />
            </div>
            <p className="text-xs text-orange-100 mt-1">Auto-sync enabled</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 h-auto p-1 gap-1 bg-gray-100 rounded-xl">
          <TabsTrigger 
            value="overview"
            className="flex items-center gap-2 py-2.5 rounded-lg data-[state=active]:bg-indigo-600 data-[state=active]:text-white"
          >
            <Database className="w-4 h-4" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger 
            value="insurance"
            className="flex items-center gap-2 py-2.5 rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white"
          >
            <Shield className="w-4 h-4" />
            <span className="hidden sm:inline">Insurance</span>
          </TabsTrigger>
          <TabsTrigger 
            value="hr"
            className="flex items-center gap-2 py-2.5 rounded-lg data-[state=active]:bg-green-600 data-[state=active]:text-white"
          >
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">HR</span>
          </TabsTrigger>
          <TabsTrigger 
            value="pm"
            className="flex items-center gap-2 py-2.5 rounded-lg data-[state=active]:bg-purple-600 data-[state=active]:text-white"
          >
            <Briefcase className="w-4 h-4" />
            <span className="hidden sm:inline">Projects</span>
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Insurance Card */}
            <Card className="border-t-4 border-t-blue-500">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-500" />
                    Insurance
                  </span>
                  <Button size="sm" variant="outline" onClick={() => openConnectModal('insurance')}>
                    <Plus className="w-4 h-4 mr-1" /> Connect
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {providers.insurance?.slice(0, 4).map((provider, i) => {
                    const isConnected = integrations.insurance?.some(int => int.provider === provider.id);
                    return (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{provider.logo}</span>
                          <span className="text-sm font-medium">{provider.name}</span>
                        </div>
                        <Badge className={isConnected ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                          {isConnected ? 'Connected' : 'Available'}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* HR Card */}
            <Card className="border-t-4 border-t-green-500">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-green-500" />
                    HR Systems
                  </span>
                  <Button size="sm" variant="outline" onClick={() => openConnectModal('hr')}>
                    <Plus className="w-4 h-4 mr-1" /> Connect
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {providers.hr?.slice(0, 4).map((provider, i) => {
                    const isConnected = integrations.hr?.some(int => int.provider === provider.id);
                    return (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{provider.logo}</span>
                          <span className="text-sm font-medium">{provider.name}</span>
                        </div>
                        <Badge className={isConnected ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                          {isConnected ? 'Connected' : 'Available'}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* PM Card */}
            <Card className="border-t-4 border-t-purple-500">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-purple-500" />
                    Project Management
                  </span>
                  <Button size="sm" variant="outline" onClick={() => openConnectModal('project_management')}>
                    <Plus className="w-4 h-4 mr-1" /> Connect
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {providers.project_management?.slice(0, 4).map((provider, i) => {
                    const isConnected = integrations.project_management?.some(int => int.provider === provider.id);
                    return (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{provider.logo}</span>
                          <span className="text-sm font-medium">{provider.name}</span>
                        </div>
                        <Badge className={isConnected ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                          {isConnected ? 'Connected' : 'Available'}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Insurance Tab */}
        <TabsContent value="insurance" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Insurance Integration</h2>
            <Button onClick={() => openConnectModal('insurance')}>
              <Plus className="w-4 h-4 mr-2" /> Add Provider
            </Button>
          </div>

          {insuranceDashboard && (
            <>
              {/* Insurance Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <FileText className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                    <p className="text-2xl font-bold">{insuranceDashboard.summary?.total_claims || 0}</p>
                    <p className="text-xs text-gray-500">Total Claims</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <AlertTriangle className="w-8 h-8 mx-auto text-orange-500 mb-2" />
                    <p className="text-2xl font-bold">{insuranceDashboard.summary?.open_claims || 0}</p>
                    <p className="text-xs text-gray-500">Open Claims</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Shield className="w-8 h-8 mx-auto text-green-500 mb-2" />
                    <p className="text-2xl font-bold">{insuranceDashboard.summary?.active_policies || 0}</p>
                    <p className="text-xs text-gray-500">Active Policies</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <DollarSign className="w-8 h-8 mx-auto text-purple-500 mb-2" />
                    <p className="text-2xl font-bold">{formatCurrency(insuranceDashboard.summary?.total_coverage)}</p>
                    <p className="text-xs text-gray-500">Total Coverage</p>
                  </CardContent>
                </Card>
              </div>

              {/* Claims Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>Claims Financial Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-gray-600">Estimated</p>
                      <p className="text-xl font-bold text-blue-600">
                        {formatCurrency(insuranceDashboard.summary?.claims_amount?.estimated)}
                      </p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="text-sm text-gray-600">Approved</p>
                      <p className="text-xl font-bold text-green-600">
                        {formatCurrency(insuranceDashboard.summary?.claims_amount?.approved)}
                      </p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <p className="text-sm text-gray-600">Paid</p>
                      <p className="text-xl font-bold text-purple-600">
                        {formatCurrency(insuranceDashboard.summary?.claims_amount?.paid)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Available Providers */}
          <Card>
            <CardHeader>
              <CardTitle>Available Insurance Providers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {providers.insurance?.map((provider, i) => (
                  <div key={i} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-3xl">{provider.logo}</span>
                      <div>
                        <p className="font-semibold">{provider.name}</p>
                        <Badge variant="outline" className="text-xs">Insurance</Badge>
                      </div>
                    </div>
                    <div className="space-y-1 mb-3">
                      {provider.features?.map((f, j) => (
                        <p key={j} className="text-xs text-gray-500 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-500" /> {f}
                        </p>
                      ))}
                    </div>
                    <Button 
                      size="sm" 
                      className="w-full"
                      onClick={() => {
                        setConnectingType('insurance');
                        setSelectedProvider(provider.id);
                        setShowConnectModal(true);
                      }}
                    >
                      <Link2 className="w-4 h-4 mr-2" /> Connect
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* HR Tab */}
        <TabsContent value="hr" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">HR System Integration</h2>
            <Button onClick={() => openConnectModal('hr')}>
              <Plus className="w-4 h-4 mr-2" /> Add HR System
            </Button>
          </div>

          {hrDashboard && (
            <>
              {/* HR Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <Users className="w-8 h-8 mx-auto text-green-500 mb-2" />
                    <p className="text-2xl font-bold">{hrDashboard.summary?.total_active_employees || 0}</p>
                    <p className="text-xs text-gray-500">Active Employees</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Building className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                    <p className="text-2xl font-bold">{hrDashboard.summary?.departments || 0}</p>
                    <p className="text-xs text-gray-500">Departments</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <FileText className="w-8 h-8 mx-auto text-purple-500 mb-2" />
                    <p className="text-2xl font-bold">{hrDashboard.summary?.total_certifications || 0}</p>
                    <p className="text-xs text-gray-500">Certifications</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <TrendingUp className="w-8 h-8 mx-auto text-orange-500 mb-2" />
                    <p className="text-2xl font-bold">{hrDashboard.summary?.total_training_records || 0}</p>
                    <p className="text-xs text-gray-500">Training Records</p>
                  </CardContent>
                </Card>
              </div>

              {/* Department Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Department Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(hrDashboard.department_breakdown || {}).map(([dept, count], i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="font-medium">{dept}</span>
                        <div className="flex items-center gap-2">
                          <Progress value={(count / hrDashboard.summary?.total_active_employees) * 100} className="w-32 h-2" />
                          <span className="text-sm text-gray-500 w-12 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Available HR Providers */}
          <Card>
            <CardHeader>
              <CardTitle>Available HR Systems</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {providers.hr?.map((provider, i) => (
                  <div key={i} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-3xl">{provider.logo}</span>
                      <div>
                        <p className="font-semibold">{provider.name}</p>
                        <Badge variant="outline" className="text-xs">HR System</Badge>
                      </div>
                    </div>
                    <div className="space-y-1 mb-3">
                      {provider.features?.map((f, j) => (
                        <p key={j} className="text-xs text-gray-500 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-500" /> {f}
                        </p>
                      ))}
                    </div>
                    <Button 
                      size="sm" 
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => {
                        setConnectingType('hr');
                        setSelectedProvider(provider.id);
                        setShowConnectModal(true);
                      }}
                    >
                      <Link2 className="w-4 h-4 mr-2" /> Connect
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Project Management Tab */}
        <TabsContent value="pm" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Project Management Integration</h2>
            <Button onClick={() => openConnectModal('project_management')}>
              <Plus className="w-4 h-4 mr-2" /> Add PM Tool
            </Button>
          </div>

          {pmDashboard && (
            <>
              {/* PM Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <ClipboardList className="w-8 h-8 mx-auto text-purple-500 mb-2" />
                    <p className="text-2xl font-bold">{pmDashboard.summary?.total_tasks || 0}</p>
                    <p className="text-xs text-gray-500">Total Tasks</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Clock className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                    <p className="text-2xl font-bold">{pmDashboard.summary?.open_tasks || 0}</p>
                    <p className="text-xs text-gray-500">Open Tasks</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <AlertCircle className="w-8 h-8 mx-auto text-red-500 mb-2" />
                    <p className="text-2xl font-bold">{pmDashboard.summary?.overdue_tasks || 0}</p>
                    <p className="text-xs text-gray-500">Overdue</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <CheckCircle className="w-8 h-8 mx-auto text-green-500 mb-2" />
                    <p className="text-2xl font-bold">{pmDashboard.summary?.completed_this_month || 0}</p>
                    <p className="text-xs text-gray-500">Completed</p>
                  </CardContent>
                </Card>
              </div>

              {/* Task Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Tasks by Type</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(pmDashboard.summary?.by_type || {}).map(([type, count], i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="capitalize">{type.replace('_', ' ')}</span>
                          <Badge>{count}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Tasks by Priority</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(pmDashboard.summary?.by_priority || {}).map(([priority, count], i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <Badge className={
                            priority === 'critical' ? 'bg-red-100 text-red-800' :
                            priority === 'high' ? 'bg-orange-100 text-orange-800' :
                            priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }>
                            {priority}
                          </Badge>
                          <span className="font-semibold">{count}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}

          {/* Available PM Providers */}
          <Card>
            <CardHeader>
              <CardTitle>Available Project Management Tools</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {providers.project_management?.map((provider, i) => (
                  <div key={i} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-3xl">{provider.logo}</span>
                      <div>
                        <p className="font-semibold">{provider.name}</p>
                        <Badge variant="outline" className="text-xs">Project Management</Badge>
                      </div>
                    </div>
                    <div className="space-y-1 mb-3">
                      {provider.features?.map((f, j) => (
                        <p key={j} className="text-xs text-gray-500 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-500" /> {f}
                        </p>
                      ))}
                    </div>
                    <Button 
                      size="sm" 
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      onClick={() => {
                        setConnectingType('project_management');
                        setSelectedProvider(provider.id);
                        setShowConnectModal(true);
                      }}
                    >
                      <Link2 className="w-4 h-4 mr-2" /> Connect
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Connect Modal */}
      {showConnectModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="w-5 h-5" />
                Connect {connectingType?.replace('_', ' ')}
              </CardTitle>
              <CardDescription>
                Enter your API credentials to connect
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Provider</Label>
                <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {providers[connectingType]?.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.logo} {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>API Key (Optional)</Label>
                <Input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter API key"
                />
              </div>
              <div>
                <Label>API Secret (Optional)</Label>
                <Input
                  type="password"
                  value={apiSecret}
                  onChange={(e) => setApiSecret(e.target.value)}
                  placeholder="Enter API secret"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={connectIntegration} className="flex-1">
                  <CheckCircle className="w-4 h-4 mr-2" /> Connect
                </Button>
                <Button variant="outline" onClick={() => setShowConnectModal(false)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default IntegrationsHub;
